export const DEFAULT_HERO_IMAGE_CONFIG = {
  minItems: 36,
  maxItems: 40,
  initialLoadCount: 12,
  lazyLoadBatchSize: 4,
  lazyLoadDelay: 180,
  duplicateTextureUniqueMax: 1,
  duplicateTextureStrictMinPoolSize: 28,
  duplicateTextureStrictMax: 2,
  duplicateTextureRelaxedMax: 3,
  selection: 'first',
  seed: 'floema-hero',
  preferredFormats: ['webp', 'avif'],
};

const TAU = Math.PI * 2;

export function resolveImageResource(image, index = 0, options = {}) {
  const preferredFormats =
    options.preferredFormats ?? DEFAULT_HERO_IMAGE_CONFIG.preferredFormats;

  if (typeof image === 'string') {
    return {
      id: `image-${index}`,
      src: image,
      fallbackSrc: image,
      srcset: '',
      sizes: '',
      alt: '',
      width: undefined,
      height: undefined,
      textureKey: image,
      raw: image,
    };
  }

  const sources = image?.sources ?? {};
  const topLevelSources = {
    avif: image?.avif,
    webp: image?.webp,
  };
  const sourceCandidates = { ...sources, ...topLevelSources };
  const preferredSource = preferredFormats
    .map((format) => sourceCandidates[format])
    .find(Boolean);
  const fallbackSrc =
    image?.src ??
    preferredSource ??
    sourceCandidates.jpg ??
    sourceCandidates.jpeg ??
    sourceCandidates.png ??
    '';
  const src = preferredSource ?? fallbackSrc;
  const srcset = image?.srcset ?? sourceCandidates.srcset ?? '';
  const sizes = image?.sizes ?? '';
  const textureKey = [src, srcset, sizes].filter(Boolean).join('|');

  return {
    id: image?.id ?? `image-${index}`,
    src,
    fallbackSrc,
    srcset,
    sizes,
    alt: image?.alt ?? '',
    width: image?.width,
    height: image?.height,
    textureKey,
    raw: image,
  };
}

export function prepareSceneImages(backendImages = [], options = {}) {
  const config = normalizeConfig(options);
  const resources = avoidPreferredSourceCollisions(
    backendImages
      .map((image, index) => ({
        ...resolveImageResource(image, index, config),
        sourceIndex: index,
      }))
      .filter((resource) => resource.src)
  );

  if (resources.length === 0) {
    return [];
  }

  const selectedResources = selectResources(resources, config);
  const targetCount =
    selectedResources.length < config.minItems
      ? config.minItems
      : selectedResources.length;
  const sceneResources = [];
  const initialIndexSet = getDistributedInitialIndexSet(
    targetCount,
    config.initialLoadCount
  );

  for (let index = 0; index < targetCount; index += 1) {
    sceneResources.push(selectedResources[index % selectedResources.length]);
  }

  return sceneResources.map((resource, slotIndex) => {
    const duplicateIndex = Math.floor(slotIndex / selectedResources.length);
    const random = createRandom(`${config.seed}:${resource.textureKey}:${slotIndex}`);
    const transform = createSceneTransform(
      slotIndex,
      sceneResources.length,
      random,
      resource
    );

    return {
      id: `${resource.id}--slot-${slotIndex}`,
      sourceId: resource.id,
      sourceIndex: resource.sourceIndex,
      slotIndex,
      duplicateIndex,
      isDuplicate: duplicateIndex > 0,
      textureKey: resource.textureKey,
      resource,
      loadPriority: initialIndexSet.has(slotIndex) ? 'initial' : 'lazy',
      ...transform,
    };
  });
}

export function createSceneLoadQueue(sceneImages = [], options = {}) {
  const initialLoadCount = Math.max(
    1,
    Math.floor(options.initialLoadCount ?? DEFAULT_HERO_IMAGE_CONFIG.initialLoadCount)
  );
  const lazyLoadBatchSize = Math.max(
    1,
    Math.floor(
      options.lazyLoadBatchSize ?? DEFAULT_HERO_IMAGE_CONFIG.lazyLoadBatchSize
    )
  );
  const initialIndexSet = getDistributedInitialIndexSet(
    sceneImages.length,
    initialLoadCount
  );
  const initial = [];
  const lazyItems = [];
  const lazy = [];

  sceneImages.forEach((item, index) => {
    if (initialIndexSet.has(index)) {
      initial.push(item);
    } else {
      lazyItems.push(item);
    }
  });

  for (let index = 0; index < lazyItems.length; index += lazyLoadBatchSize) {
    lazy.push(lazyItems.slice(index, index + lazyLoadBatchSize));
  }

  return { initial, lazy };
}

function getDistributedInitialIndexSet(count, initialLoadCount) {
  const total = Math.max(0, Math.floor(count));
  const target = Math.min(
    total,
    Math.max(1, Math.floor(initialLoadCount ?? DEFAULT_HERO_IMAGE_CONFIG.initialLoadCount))
  );
  const selected = new Set();

  if (!total) {
    return selected;
  }

  for (let step = 0; step < target; step += 1) {
    let index = Math.min(total - 1, Math.floor((step * total) / target));

    while (selected.has(index) && selected.size < target) {
      index = (index + 1) % total;
    }

    selected.add(index);
  }

  return selected;
}

function normalizeConfig(options) {
  const merged = { ...DEFAULT_HERO_IMAGE_CONFIG, ...options };
  const maxItems = Math.max(1, Math.floor(merged.maxItems));
  const minItems = Math.max(1, Math.min(Math.floor(merged.minItems), maxItems));
  const initialLoadCount = Math.max(
    1,
    Math.min(Math.floor(merged.initialLoadCount), maxItems)
  );
  const lazyLoadBatchSize = Math.max(1, Math.floor(merged.lazyLoadBatchSize));
  const lazyLoadDelay = Math.max(0, Math.floor(merged.lazyLoadDelay));

  return {
    ...merged,
    minItems,
    maxItems,
    initialLoadCount,
    lazyLoadBatchSize,
    lazyLoadDelay,
  };
}

function selectResources(resources, config) {
  if (resources.length <= config.maxItems) {
    return resources;
  }

  if (config.selection === 'random') {
    const random = createRandom(`${config.seed}:selection`);
    const shuffled = [...resources];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [
        shuffled[swapIndex],
        shuffled[index],
      ];
    }

    return shuffled.slice(0, config.maxItems);
  }

  return resources.slice(0, config.maxItems);
}

function avoidPreferredSourceCollisions(resources) {
  const groups = new Map();

  for (const resource of resources) {
    const group = groups.get(resource.src) ?? [];
    group.push(resource);
    groups.set(resource.src, group);
  }

  const collidingSources = new Set();

  for (const [src, group] of groups) {
    const fallbackSources = new Set(
      group.map((resource) => resource.fallbackSrc).filter(Boolean)
    );

    if (group.length > 1 && fallbackSources.size > 1) {
      collidingSources.add(src);
    }
  }

  if (collidingSources.size === 0) {
    return resources;
  }

  return resources.map((resource) => {
    if (
      !collidingSources.has(resource.src) ||
      !resource.fallbackSrc ||
      resource.fallbackSrc === resource.src
    ) {
      return resource;
    }

    return {
      ...resource,
      src: resource.fallbackSrc,
      srcset: '',
      sizes: '',
      textureKey: resource.fallbackSrc,
    };
  });
}

function createSceneTransform(slotIndex, count, random, resource) {
  const angle = (slotIndex / count) * TAU + randomBetween(random, -0.2, 0.2);
  const screenRadiusX = randomBetween(random, 0.18, 0.5);
  const screenRadiusY = randomBetween(random, 0.13, 0.45);
  let screenX =
    Math.cos(angle) * screenRadiusX + randomBetween(random, -0.035, 0.035);
  let screenY =
    Math.sin(angle) * screenRadiusY + randomBetween(random, -0.035, 0.035);
  const edgeItem = slotIndex % 7 === 0 || slotIndex % 11 === 0;

  if (Math.abs(screenX) < 0.22 && Math.abs(screenY) < 0.16) {
    const xFactor = Math.abs(screenX) > 0.01 ? 0.22 / Math.abs(screenX) : 1.4;
    const yFactor = Math.abs(screenY) > 0.01 ? 0.16 / Math.abs(screenY) : 1.4;
    const factor = Math.max(xFactor, yFactor) + randomBetween(random, 0.2, 0.55);
    screenX *= factor;
    screenY *= factor;
  }

  if (edgeItem) {
    screenX = clamp(screenX * randomBetween(random, 1.08, 1.22), -0.56, 0.56);
    screenY = clamp(screenY * randomBetween(random, 1.05, 1.18), -0.52, 0.52);
  }

  const depthLayer = slotIndex % 4;
  const layerDepth = [-1.15, -0.36, 0.42, 1.1][depthLayer];
  const z = layerDepth + randomBetween(random, -0.22, 0.22);
  const depthRatio = clamp((z + 1.45) / 2.85, 0, 1);
  const aspectRatio = resolveAspectRatio(resource, random);
  const scale =
    randomBetween(random, 0.38, 0.72) +
    depthRatio * 0.2 +
    (edgeItem ? randomBetween(random, 0.32, 0.58) : 0);
  const opacity = clamp(
    randomBetween(random, 0.28, 0.56) + depthRatio * 0.34,
    0.22,
    0.92
  );
  const x = screenX * 9.6;
  const y = screenY * 5.4;

  return {
    screenPosition: {
      x: round(screenX),
      y: round(screenY),
    },
    position: {
      x: round(x),
      y: round(y),
      z: round(z),
    },
    rotation: {
      x: round(randomBetween(random, -0.045, 0.045)),
      y: round(randomBetween(random, -0.065, 0.065)),
      z: round(randomBetween(random, -0.2, 0.2)),
    },
    scale: round(scale),
    opacity: round(opacity),
    aspectRatio: round(aspectRatio),
    depth: round(z),
  };
}

function resolveAspectRatio(resource, random) {
  if (resource.width && resource.height) {
    return clamp(resource.width / resource.height, 0.62, 1.58);
  }

  return randomBetween(random, 0.72, 1.34);
}

function createRandom(seed) {
  let state = hashString(seed);

  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function randomBetween(random, min, max) {
  return min + (max - min) * random();
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function round(value) {
  return Math.round(value * 10000) / 10000;
}
